import React, { useEffect, FC, useState, useMemo } from 'react';
import { Theme, styled } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Loading } from 'src/modules/shared/components';
import { useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import { ToolboxAction } from '../TrajectoryDetail/TrajectoryToolbox/types';

const DEFAULT_PADDING = 20;

const BoxWrapper = styled('div')<Theme, { width?: number | string }>(({ theme, width }) => ({
  background: theme.palette.common.white,
  height: '100vh',
  border: '1px solid transparent',
  position: 'relative',
}));

const StyledGeogebraFrame = styled('div')<Theme, { width?: string | number }>(
  ({ theme, width }) => ({
    height: '120vh',
    width: width,
    '.fullscreen &': {
      height: '100vh',
    },
    '& .GeoGebraFrame': {
      '&.applet-unfocused': {
        borderColor: 'rgba(211, 211, 211, 0.5)',
      },
      '&.applet-focused': {
        borderColor: 'rgba(211, 211, 211, 1)',
      },
    },
    '&.is-algebraic-calc': {
      '& .GeoGebraFrame': {
        '& .draggerParentVertical': {
          display: 'none',
        },
        '& .matDragger': {
          '& .EuclidianPanel': {
            display: 'none',
          },
          '& > *': {
            '&:last-child': {
              inset: '50px 0px 0px !important',
              borderTop: '1px solid black',
            },
          },
        },
      },
    },
  })
);

const GeoGebraCalculator: FC = () => {
  const { t } = useTranslation();
  // const theme = useTheme();
  // const headerHeight = theme.config.appbarHeight + theme.config.subHeaderHeight;

  // redux
  const geogebraAppName = useAppSelector((state) => state.geogebra.geogebraAppName);
  const sidepanel = useAppSelector((state) => state.sidepanel.sidepanel);

  // state
  const [calcReady, setCalcReady] = useState(false);

  const nameMapper = useMemo(() => {
    return {
      [ToolboxAction.GEOGEBRA_SCIENTIFIC]: 'scientific',
      [ToolboxAction.GEOGEBRA_GRAPHING]: 'graphing',
      [ToolboxAction.GEOGEBRA_CAS]: 'cas',
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const windowHeight = window.innerHeight - DEFAULT_PADDING;
      // @ts-ignore
      window.ggbApplet && // @ts-ignore
        window.ggbApplet.setSize(sidepanel?.width - DEFAULT_PADDING, windowHeight);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [sidepanel]);

  useEffect(() => {
    if (!!geogebraAppName) {
      let parameters = {
        appName: nameMapper[geogebraAppName as keyof typeof nameMapper],
        width: 320,
        height: 740,
        autoHeight: false,
        showToolBar: true,
        borderColor: null,
        showMenuBar: true,
        allowStyleBar: true,
        showAlgebraInput: true,
        showToolBarHelp: true,
        disableAutoScale: true,
      };

      setTimeout(function () {
        setCalcReady(true);
        // @ts-ignore
        window.GGBApplet(parameters, true).inject('ggb-app');
      }, 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geogebraAppName, nameMapper]);
  //@ts-ignore

  useEffect(() => {
    // @ts-ignore
    if (typeof window.GGBApplet === 'undefined') {
      // setCalcReady(true);
      const script = document.createElement('script');
      script.src = 'https://www.geogebra.org/apps/latest/deployggb.js';
      script.async = true;
      document.body.appendChild(script);
    }
    // @ts-ignore
  }, []);

  const loadingMessage = () => {
    // TODO - CAS calculator needs translation to remove next line
    if (geogebraAppName === ToolboxAction.GEOGEBRA_CAS) return 'CAS rekentoestel laden ...';
    return `${t(
      `trajectory.geogebra.${nameMapper[geogebraAppName as keyof typeof nameMapper]}`
    )} laden ...`;
  };

  // render
  if (!geogebraAppName) return null;
  return (
    <BoxWrapper width={sidepanel?.width}>
      {(!calcReady && <Loading withSkeleton message={loadingMessage()} />) || (
        <StyledGeogebraFrame
          id="ggb-app"
          className={geogebraAppName === ToolboxAction.GEOGEBRA_CAS ? 'is-algebraic-calc' : ''}
          width={sidepanel?.width}
        />
      )}
    </BoxWrapper>
  );
};

export default GeoGebraCalculator;
